import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, tap } from 'rxjs/operators';
import {
  IBillingDocument,
  IBillingTransactions,
  IBillingAccount,
  IInstalmentPlanInformation,
  PaymentStatus,
  coverService,
} from '@dch/billing-business-logic';
import { ApplicationState } from 'src/app/state/reducers';
import { TranslationService } from '@dch/ng-common';
import { selectSelectedBillingAccount } from 'src/app/state/selectors/billing-balance.selector';
import { OnDestroyHookDirective } from '../common/hooks';
import { BillingAnalyticsService } from '../services/billing-analytics.service';
import { selectCustomerAccount } from 'src/app/state/selectors/customer-account.selector';

@Component({
  selector: 'my-bill-instalment-plan-details-page',
  templateUrl: './my-bill-instalment-plan-details-page.component.html',
  standalone: false,
})
export class MyBillInstalmentPlanDetailsPageComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  mainTitle: string;
  subtitle: string;
  instalmentPlanInformation: IInstalmentPlanInformation;
  transactions: IBillingTransactions;
  billingDocuments: IBillingDocument[];
  billingAccount: IBillingAccount;
  isResUser = true;
  concernedBillsKey = '';

  readonly selectedBillingAccount$ = this.store.select(
    selectSelectedBillingAccount
  );

  constructor(
    private translationService: TranslationService,
    private router: Router,
    private store: Store<ApplicationState>,
    private billingAnalyticsService: BillingAnalyticsService
  ) {
    super();
    const currentNavigation = this.router.getCurrentNavigation();
    this.instalmentPlanInformation =
      currentNavigation?.extras.state?.instalmentPlanInformation;
    this.transactions = currentNavigation?.extras.state?.transactions;
    if (this.transactions) {
      this.billingDocuments =
        this.transactions.openTransactions.billingDocuments.filter(
          (billingDocument: IBillingDocument) =>
            billingDocument.paymentStatus === PaymentStatus.INSTALMENT
        );
    }
  }

  ngOnInit() {
    this.mainTitle = this.translationService.getTranslatedValueFor(
      'bills.instalment.detailsPage.title'
    );
    this.subtitle = `${this.translationService.getTranslatedValueFor(
      'bills.instalment.detailsPage.requestedOn'
    )} ${this.instalmentPlanInformation?.creationDate}`;
    this.getSelectedBillingAccount();
    this.reportAnalyticsInfo();
  }

  getMonthTranslation(dateInput: string) {
    const date = dateInput.split('/');
    return coverService.getMonthTranslationsByMonthNumber(
      Number(date[1]) - 1,
      (key: string) => this.translationService.getTranslatedValueFor(key)
    );
  }

  private getSelectedBillingAccount() {
    this.addSubscription(
      this.selectedBillingAccount$
        .pipe(
          filter(
            (selectedBillingAccount) => selectedBillingAccount !== undefined
          ),
          tap((selectedBillingAccount) => {
            this.billingAccount = selectedBillingAccount;
          })
        )
        .subscribe()
    );
  }

  private reportAnalyticsInfo() {
    this.addSubscription(
      this.store
        .select(selectCustomerAccount)
        .pipe(
          filter((customerAccount) => customerAccount !== undefined),
          tap((customerAccount) => {
            this.isResUser =
              customerAccount.getSelectedCustomer().subsegment === 'RES';
            const resUserKey = this.isResUser ? 'RES.' : '';
            this.concernedBillsKey = `bills.instalment.detailsPage.${resUserKey}concernedBills`;
            return this.billingAnalyticsService.reportPageImpressionEventWithPagePrefix(
              'instalment plan',
              this.billingDocuments?.[0].reference,
              this.billingAccount.accountNumber,
              'mybill.instalment plan',
              undefined,
              customerAccount.getSelectedCustomer().customerId,
              customerAccount.getSelectedCustomer().subsegment,
              customerAccount.globalId,
              customerAccount.mobile
            );
          })
        )
        .subscribe()
    );
  }
}
